import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const fmt = (v) => (v == null ? "—" : formatCurrency(v));

export default function KpiDrillDialog({ kpi, onClose }) {
  const [data, setData] = useState(null);
  const isRecon = kpi === "net";

  useEffect(() => {
    if (!kpi) { setData(null); return; }
    const url = isRecon ? "/finance/drill/reconciliation" : `/finance/drill/${kpi}`;
    api.get(url).then(({ data: d }) => setData(d)).catch((e) => toast.error(formatApiError(e)));
  }, [kpi]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={!!kpi} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="kpi-drill-dialog">
        <DialogHeader>
          <DialogTitle>{isRecon ? "Dashboard ↔ Ledger reconciliation" : `${data?.label || kpi} — GL drill-down`}</DialogTitle>
          <DialogDescription>
            {isRecon
              ? "Every dashboard figure traced to its general-ledger source with the variance."
              : "The general-ledger accounts and latest journal lines behind this KPI."}
          </DialogDescription>
        </DialogHeader>

        {!data ? (
          <div className="py-8 text-center text-sm text-slate-500">Loading…</div>
        ) : isRecon ? (
          <table className="data-table w-full" data-testid="drill-recon-table">
            <thead><tr><th>KPI</th><th className="text-right">Dashboard</th><th className="text-right">General Ledger</th><th className="text-right">Δ Delta</th><th>Status</th></tr></thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.kpi} className="text-sm" data-testid={`drill-recon-${r.kpi}`}>
                  <td className="px-4 py-2.5">
                    <div className="font-semibold">{r.label}</div>
                    <div className="text-[10px] text-slate-400">{r.source}</div>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono-alt tabular-nums">{fmt(r.dashboard_value)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-alt tabular-nums">{fmt(r.gl_value)}</td>
                  <td className={`px-4 py-2.5 text-right font-mono-alt tabular-nums font-semibold ${r.matched ? "text-slate-400" : "text-amber-600"}`}>{fmt(r.delta)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.matched ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                      {r.matched ? "MATCHED" : "VARIANCE"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 flex items-center justify-between">
              <span className="overline">GL total ({data.side} side)</span>
              <span className="font-display text-xl font-bold tabular-nums" data-testid="drill-gl-total">{fmt(data.gl_total)}</span>
            </div>
            <div>
              <div className="overline mb-1">By account</div>
              <table className="data-table w-full">
                <thead><tr><th>Account</th><th className="text-right">Lines</th><th className="text-right">Amount</th></tr></thead>
                <tbody>
                  {data.accounts.map((a) => (
                    <tr key={a.account_code} className="text-sm">
                      <td className="px-4 py-2"><span className="font-mono-alt text-xs text-slate-400 mr-2">{a.account_code}</span>{a.account_name}</td>
                      <td className="px-4 py-2 text-right font-mono-alt tabular-nums">{a.line_count}</td>
                      <td className="px-4 py-2 text-right font-mono-alt tabular-nums font-semibold">{fmt(a.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <div className="overline mb-1">Latest journal lines</div>
              <div className="max-h-64 overflow-y-auto rounded-md border border-slate-100">
                <table className="data-table w-full">
                  <thead><tr><th>Date</th><th>Entry</th><th>Account</th><th>Description</th><th className="text-right">Dr</th><th className="text-right">Cr</th></tr></thead>
                  <tbody>
                    {data.lines.map((l) => (
                      <tr key={l.id} className="text-xs">
                        <td className="px-3 py-1.5 font-mono-alt tabular-nums">{formatDate(l.date)}</td>
                        <td className="px-3 py-1.5 font-mono-alt">{l.entry_no}</td>
                        <td className="px-3 py-1.5 font-mono-alt">{l.account_code}</td>
                        <td className="px-3 py-1.5">{l.description}</td>
                        <td className="px-3 py-1.5 text-right font-mono-alt tabular-nums">{l.debit ? fmt(l.debit) : ""}</td>
                        <td className="px-3 py-1.5 text-right font-mono-alt tabular-nums">{l.credit ? fmt(l.credit) : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
